import {
  ApiError,
  formatMoney,
  iccidFailsChecksumOnly,
  identifierRejectionReason,
  isUnitTracked,
  type ProductType,
  queryKeys,
  UNIT_IDENTIFIER_LABELS,
  unitIdentifierKind,
  VENDOR_PAYMENT_STATUSES,
  type VendorPaymentStatus,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  useToast,
  VENDOR_PAYMENT_STATUS_TONE,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Check, Package, Paperclip, Pencil, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { PaymentsCard } from '../components/payments-card.js'
import { RecordPaymentDialog, usePaymentLookups } from '../components/record-payment-dialog.js'
import { UnpaidOrderWarning } from '../components/unpaid-order-warning.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import {
  ALLOWED_DOCUMENT_TYPE_LIST,
  PURCHASE_ORDER_STATUS_TONE,
  validateInvoiceFile,
} from './purchase-orders.js'

interface OrderItem {
  id: string
  productId: string
  productName: string
  sku: string
  productType: ProductType
  qty: number
  receivedQty: number
  pendingQty: number
  unitCostCents: number | null
  landedUnitCostCents: number | null
  note: string | null
}

interface OrderDetail {
  id: string
  poNumber: string
  status: string
  vendor: string
  invoiceRef: string | null
  expectedLocationId: string | null
  expectedLocationName: string | null
  note: string | null
  shippingCents: number | null
  taxCents: number | null
  paymentStatus: VendorPaymentStatus
  allocateShipping: boolean
  allocateTax: boolean
  createdByLabel: string | null
  document: { filename: string; contentType: string; sizeBytes: number; createdAt: string } | null
  items: OrderItem[]
  createdAt: string
  updatedAt: string
}

interface OrderBalance {
  totalCents: number
  paidCents: number
  balanceCents: number
}

interface OrderEvent {
  id: string
  fromStatus: string | null
  toStatus: string
  actorLabel: string | null
  actorType: string
  note: string | null
  createdAt: string
}

interface Location {
  id: string
  name: string
  isActive: boolean
}

const OPEN = ['ordered', 'partially_received']

/** One vendor order: what is still pending, what has arrived, and the Mark received step. */
export function PurchaseOrderDetailPage() {
  const { orderId } = useParams({ from: '/authed/purchase-orders/$orderId' })
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [receiving, setReceiving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [finishing, setFinishing] = useState<'cancel' | 'close' | null>(null)
  const [recordingPayment, setRecordingPayment] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const deepLink = useSearch({ strict: false }) as { recordPayment?: boolean }

  const { data: order, isLoading } = useQuery({
    queryKey: queryKeys.purchaseOrders.detail(orderId),
    queryFn: () => api.get<OrderDetail>(`/purchase-orders/${orderId}`),
  })
  const { data: events } = useQuery({
    queryKey: queryKeys.purchaseOrders.events(orderId),
    queryFn: () => api.get<OrderEvent[]>(`/purchase-orders/${orderId}/events`),
    enabled: Boolean(order),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })

  // Payment status is only a label; what is actually owed comes from payments (t1201).
  const canReadPayments = can('expenses:read')
  const { data: balance } = useQuery({
    queryKey: queryKeys.expenses.balance(orderId),
    queryFn: () => api.get<OrderBalance>(`/outgoing-payments/order-balance/${orderId}`),
    enabled: canReadPayments,
  })
  const lookups = usePaymentLookups(recordingPayment !== null)
  const vendorId = lookups.vendors.find(
    (v) => v.name.trim().toLowerCase() === order?.vendor.trim().toLowerCase(),
  )?.id
  const paymentPrefill = useMemo(
    () => ({
      kind: 'vendor' as const,
      vendorId,
      purchaseOrderId: orderId,
      amountCents: recordingPayment ?? undefined,
    }),
    [vendorId, orderId, recordingPayment],
  )

  // Arriving from "Save and record payment" on a new order: open the payment form once.
  // biome-ignore lint/correctness/useExhaustiveDependencies: act on the link once the balance is in
  useEffect(() => {
    if (!deepLink.recordPayment) return
    if (!canReadPayments || !can('expenses:manage')) {
      void navigate({
        to: '/purchase-orders/$orderId',
        params: { orderId },
        search: {},
        replace: true,
      })
      return
    }
    if (!balance) return
    if (balance.balanceCents > 0) setRecordingPayment(balance.balanceCents)
    void navigate({
      to: '/purchase-orders/$orderId',
      params: { orderId },
      search: {},
      replace: true,
    })
  }, [deepLink.recordPayment, balance])

  const upload = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.postFile(`/purchase-orders/${orderId}/document`, formData)
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Invoice attached')
    },
    onError: (error) =>
      toast.error('Could not attach', error instanceof ApiError ? error.message : undefined),
  })

  const removeDocument = useMutation({
    mutationFn: () => api.delete(`/purchase-orders/${orderId}/document`),
    onSuccess: async () => {
      await refresh()
      toast.success('Invoice removed')
    },
    onError: (error) =>
      toast.error('Could not remove', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading || !order) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  const isOpen = OPEN.includes(order.status)
  const canWrite = can('inventory:write')
  const pendingTotal = order.items.reduce((n, i) => n + i.pendingQty, 0)
  const itemsSubtotalCents = order.items.reduce((n, i) => n + i.qty * (i.unitCostCents ?? 0), 0)
  const hasUnpricedItem = order.items.some((i) => i.unitCostCents === null)
  const orderTotalCents = itemsSubtotalCents + (order.shippingCents ?? 0) + (order.taxCents ?? 0)
  const allocating = order.allocateShipping || order.allocateTax
  const allocatedLabel = [
    order.allocateShipping ? 'Shipping' : null,
    order.allocateTax ? 'Tax' : null,
  ]
    .filter(Boolean)
    .join(' and ')

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/purchase-orders"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All purchase orders
        </Link>
      </div>

      <PageHeader
        title={order.poNumber}
        description={`${order.vendor}${order.invoiceRef ? ` · ${order.invoiceRef}` : ''} · entered ${new Date(
          order.createdAt,
        ).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="purchase_order" itemId={orderId} />
            <Badge tone={PURCHASE_ORDER_STATUS_TONE[order.status] ?? 'neutral'}>
              {humanizeStatus(order.status)}
            </Badge>
            {canWrite && isOpen ? (
              <>
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                  <Pencil className="size-3.5" aria-hidden="true" /> Edit
                </Button>
                {order.status === 'ordered' ? (
                  <Button size="sm" variant="ghost" onClick={() => setFinishing('cancel')}>
                    Cancel order
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setFinishing('close')}>
                    Close — rest not coming
                  </Button>
                )}
                <Button size="sm" variant="primary" onClick={() => setReceiving(true)}>
                  <Check className="size-3.5" aria-hidden="true" /> Mark received
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {isOpen ? (
        <Alert tone="info" className="mb-4">
          {pendingTotal} {pendingTotal === 1 ? 'item is' : 'items are'} pending — on order, not in
          stock. They become inventory when you mark them received.
        </Alert>
      ) : (
        <Alert tone="info" className="mb-4">
          This order is {humanizeStatus(order.status).toLowerCase()}. Nothing further can be done to
          it.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-4" aria-hidden="true" /> Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                  <tr>
                    <th className="px-4 py-2 text-start font-medium">Product</th>
                    <th className="px-4 py-2 text-end font-medium">Ordered</th>
                    <th className="px-4 py-2 text-end font-medium">Received</th>
                    <th className="px-4 py-2 text-end font-medium">Pending</th>
                    <th className="px-4 py-2 text-end font-medium">Unit cost</th>
                    {allocating ? (
                      <th className="px-4 py-2 text-end font-medium">With shipping/tax</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5">
                        <p className="font-medium">{item.productName}</p>
                        <p className="font-identifier text-2xs text-foreground-muted">{item.sku}</p>
                      </td>
                      <td className="px-4 py-2.5 text-end tabular">{item.qty}</td>
                      <td className="px-4 py-2.5 text-end tabular">{item.receivedQty}</td>
                      <td className="px-4 py-2.5 text-end tabular">
                        {item.pendingQty > 0 ? (
                          <Badge tone="warning">{item.pendingQty} pending</Badge>
                        ) : (
                          <Badge tone="success">Received</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-end tabular">
                        {item.unitCostCents !== null ? formatMoney(item.unitCostCents) : '—'}
                      </td>
                      {allocating ? (
                        <td className="px-4 py-2.5 text-end tabular">
                          {item.landedUnitCostCents !== null
                            ? formatMoney(item.landedUnitCostCents)
                            : '—'}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-border">
                  {[
                    ['Items subtotal', itemsSubtotalCents],
                    ['Shipping', order.shippingCents],
                    ['Tax', order.taxCents],
                  ].map(([label, cents]) => (
                    <tr key={label as string}>
                      <td
                        colSpan={allocating ? 5 : 4}
                        className="px-4 py-1.5 text-end text-foreground-muted"
                      >
                        {label}
                      </td>
                      <td className="px-4 py-1.5 text-end tabular">
                        {cents === null ? '—' : formatMoney(cents as number)}
                      </td>
                    </tr>
                  ))}
                  <tr className="font-medium">
                    <td colSpan={allocating ? 5 : 4} className="px-4 py-2 text-end">
                      Total
                    </td>
                    <td className="px-4 py-2 text-end tabular">
                      {formatMoney(orderTotalCents)}
                      {hasUnpricedItem ? (
                        <span className="block text-2xs font-normal text-foreground-muted">
                          excludes items with no cost
                        </span>
                      ) : null}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>

          <PaymentsCard vendorName={order.vendor} purchaseOrderId={order.id} />

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus && event.fromStatus !== event.toStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Vendor</p>
                <p className="font-medium">{order.vendor}</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                  Invoice / PO reference
                </p>
                <p>{order.invoiceRef ?? '—'}</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Payment</p>
                <Badge tone={VENDOR_PAYMENT_STATUS_TONE[order.paymentStatus]}>
                  {humanizeStatus(order.paymentStatus)}
                </Badge>
                <p className="mt-1 text-2xs text-foreground-muted">
                  Cost entries made on receiving start as {order.paymentStatus}.
                </p>
                {order.paymentStatus === 'paid' && balance && balance.balanceCents > 0 ? (
                  <div className="mt-2">
                    <UnpaidOrderWarning
                      owedCents={balance.balanceCents}
                      onRecord={
                        can('expenses:manage')
                          ? () => setRecordingPayment(balance.balanceCents)
                          : undefined
                      }
                    />
                  </div>
                ) : null}
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                  Added to item cost
                </p>
                <p>{allocating ? `${allocatedLabel}, split by item value` : 'Nothing added'}</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-wide text-foreground-subtle">
                  Arriving at
                </p>
                <p>{order.expectedLocationName ?? 'Not set'}</p>
              </div>
              {order.note ? (
                <div>
                  <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Note</p>
                  <p className="whitespace-pre-wrap">{order.note}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Invoice file</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {order.document ? (
                <a
                  href={`${apiBaseUrl}/purchase-orders/${order.id}/document`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 font-medium underline"
                >
                  <Paperclip className="size-3.5" aria-hidden="true" /> {order.document.filename}
                </a>
              ) : (
                <p className="text-foreground-muted">No file attached.</p>
              )}
              {canWrite ? (
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_DOCUMENT_TYPE_LIST}
                    className="hidden"
                    onChange={(e) => {
                      const picked = e.target.files?.[0] ?? null
                      if (picked && validateInvoiceFile(picked, toast)) upload.mutate(picked)
                      e.target.value = ''
                    }}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={upload.isPending}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {order.document ? 'Replace' : 'Attach'}
                  </Button>
                  {order.document ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={removeDocument.isPending}
                      onClick={() => removeDocument.mutate()}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <ReceiveDialog order={order} open={receiving} onClose={() => setReceiving(false)} />
      <EditDialog
        key={order.updatedAt}
        order={order}
        open={editing}
        onClose={() => setEditing(false)}
        onRecordPayment={setRecordingPayment}
      />
      <RecordPaymentDialog
        open={recordingPayment !== null}
        onClose={() => setRecordingPayment(null)}
        prefill={paymentPrefill}
      />
      <FinishDialog order={order} action={finishing} onClose={() => setFinishing(null)} />
    </Page>
  )
}

type ReceiveLine =
  | {
      itemId: string
      units: Array<{ imei: string; serialNumber: null; acceptBadChecksum: boolean }>
    }
  | { itemId: string; qty: number }

interface Scan {
  imei: string
  acceptBadChecksum: boolean
  /** Set when an ICCID fails only its check digit and still needs the receiver's OK. */
  needsAccept: boolean
}

function ReceiveDialog({
  order,
  open,
  onClose,
}: {
  order: OrderDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [locationId, setLocationId] = useState('')
  const [qtys, setQtys] = useState<Record<string, string>>({})
  const [scans, setScans] = useState<Record<string, Scan[]>>({})
  const [inputs, setInputs] = useState<Record<string, string>>({})
  const [problems, setProblems] = useState<Array<{ imei: string; reason: string }>>([])
  const [markPaid, setMarkPaid] = useState(false)
  const alreadyPaid = order.paymentStatus === 'paid'

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
    enabled: open,
  })
  const activeLocations = (locations ?? []).filter((l) => l.isActive)
  const resolvedLocationId = locationId || order.expectedLocationId || activeLocations[0]?.id || ''

  const pendingItems = order.items.filter((i) => i.pendingQty > 0)

  const qtyFor = (item: OrderItem) => qtys[item.id] ?? String(item.pendingQty)

  /** Either the scan a typed value becomes, or why it can't go on the line. */
  function checkScan(
    item: OrderItem,
    value: string,
    existing: Scan[],
  ): { scan: Scan } | { error: { title: string; message: string } } {
    const kind = unitIdentifierKind(item.productType)
    const reason = identifierRejectionReason(kind, value)
    const checksumOnly = reason !== null && kind === 'iccid' && iccidFailsChecksumOnly(value)

    if (existing.some((s) => s.imei === value)) {
      return { error: { title: 'Scanned twice', message: `${value} is already on this line.` } }
    }
    if (existing.length >= item.pendingQty) {
      return {
        error: {
          title: 'More than was ordered',
          message: `Only ${item.pendingQty} of ${item.productName} pending.`,
        },
      }
    }
    if (reason && !checksumOnly) {
      return { error: { title: 'Not accepted', message: `${value}: ${reason}` } }
    }
    return { scan: { imei: value, acceptBadChecksum: false, needsAccept: checksumOnly } }
  }

  function addScan(item: OrderItem) {
    const value = (inputs[item.id] ?? '').trim()
    if (!value) return
    const existing = scans[item.id] ?? []
    const result = checkScan(item, value, existing)
    if ('error' in result) {
      toast.error(result.error.title, result.error.message)
    } else {
      setScans((current) => ({ ...current, [item.id]: [...existing, result.scan] }))
    }
    setInputs((current) => ({ ...current, [item.id]: '' }))
  }

  const buildLines = (scanned: Record<string, Scan[]>) =>
    pendingItems.flatMap((item): ReceiveLine[] => {
      if (isUnitTracked(item.productType)) {
        const units = (scanned[item.id] ?? []).filter((s) => !s.needsAccept || s.acceptBadChecksum)
        return units.length > 0
          ? [
              {
                itemId: item.id,
                units: units.map((s) => ({
                  imei: s.imei,
                  serialNumber: null,
                  acceptBadChecksum: s.acceptBadChecksum,
                })),
              },
            ]
          : []
      }
      const qty = Number.parseInt(qtyFor(item), 10)
      return qty > 0 ? [{ itemId: item.id, qty }] : []
    })
  const lines = buildLines(scans)
  // A number typed into a line but not yet added still counts when Receive is pressed.
  const hasTyped = pendingItems.some(
    (i) => isUnitTracked(i.productType) && (inputs[i.id] ?? '').trim() !== '',
  )
  const unresolved = Object.values(scans)
    .flat()
    .some((s) => s.needsAccept && !s.acceptBadChecksum)

  const receive = useMutation({
    mutationFn: (payload: ReceiveLine[]) =>
      api.post<{
        receivedUnits: number
        receivedQty: number
        rejected: Array<{ itemId: string; imei: string; reason: string }>
        status: string
      }>(`/purchase-orders/${order.id}/receive`, {
        locationId: resolvedLocationId,
        note: null,
        markPaid: markPaid && !alreadyPaid,
        lines: payload,
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() }),
      ])
      const total = result.receivedUnits + result.receivedQty
      toast.success(`Received ${total} ${total === 1 ? 'item' : 'items'}`)
      if (result.rejected.length === 0) {
        setScans({})
        setQtys({})
        setProblems([])
        onClose()
        return
      }
      // Keep only what did not go in, so the rest can be fixed and retried.
      const rejectedImeis = new Set(result.rejected.map((r) => r.imei))
      setScans((current) =>
        Object.fromEntries(
          Object.entries(current).map(([id, list]) => [
            id,
            list.filter((s) => rejectedImeis.has(s.imei)),
          ]),
        ),
      )
      setQtys({})
      setProblems(result.rejected)
    },
    onError: (error) =>
      toast.error('Could not receive', error instanceof ApiError ? error.message : undefined),
  })

  /** Folds any typed-but-not-added numbers into their lines, then receives. */
  function submitReceive() {
    const next = { ...scans }
    let held = false
    for (const item of pendingItems) {
      if (!isUnitTracked(item.productType)) continue
      const value = (inputs[item.id] ?? '').trim()
      if (!value) continue
      const result = checkScan(item, value, next[item.id] ?? [])
      if ('error' in result) {
        toast.error(result.error.title, result.error.message)
        return
      }
      next[item.id] = [...(next[item.id] ?? []), result.scan]
      // An ICCID that fails only its check digit still needs the receiver's OK.
      if (result.scan.needsAccept) held = true
    }
    if (hasTyped) {
      setScans(next)
      setInputs({})
    }
    if (!held) receive.mutate(buildLines(next))
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Mark received — {order.poNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            Receive all or part of what is pending. Anything you leave out stays pending.
          </p>
          <Field label="Location">
            {(props) => (
              <Select value={resolvedLocationId} onValueChange={setLocationId}>
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Choose a location…" />
                </SelectTrigger>
                <SelectContent>
                  {activeLocations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <div className="flex items-center gap-2 text-sm">
            <Switch
              id="receive-mark-paid"
              checked={alreadyPaid || markPaid}
              disabled={alreadyPaid}
              onCheckedChange={setMarkPaid}
            />
            <label htmlFor="receive-mark-paid">
              {alreadyPaid
                ? 'This order is marked paid — cost entries are recorded as paid'
                : 'Already paid — record these costs as paid and mark the order paid'}
            </label>
          </div>
          {order.allocateShipping || order.allocateTax ? (
            <p className="text-xs text-foreground-muted">
              {[order.allocateShipping ? 'Shipping' : null, order.allocateTax ? 'Tax' : null]
                .filter(Boolean)
                .join(' and ')}{' '}
              is added to each item's cost, split by item value.
            </p>
          ) : null}

          {problems.length > 0 ? (
            <Alert tone="warning">
              <p className="font-medium">Some scans were not received:</p>
              <ul className="mt-1 list-disc ps-5 text-sm">
                {problems.map((p) => (
                  <li key={p.imei}>
                    <span className="font-identifier">{p.imei}</span> — {p.reason}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}

          {pendingItems.map((item) => {
            const tracked = isUnitTracked(item.productType)
            const kindLabel = UNIT_IDENTIFIER_LABELS[unitIdentifierKind(item.productType)]
            const list = scans[item.id] ?? []
            return (
              <div key={item.id} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{item.productName}</p>
                    <p className="text-xs text-foreground-muted">
                      {item.pendingQty} pending of {item.qty}
                    </p>
                  </div>
                  {tracked ? (
                    <span className="text-xs text-foreground-muted tabular">
                      {list.length} scanned
                    </span>
                  ) : (
                    <Input
                      type="number"
                      min={0}
                      max={item.pendingQty}
                      value={qtyFor(item)}
                      aria-label={`Quantity received of ${item.productName}`}
                      onChange={(e) =>
                        setQtys((current) => ({ ...current, [item.id]: e.target.value }))
                      }
                      className="w-20 text-end"
                    />
                  )}
                </div>
                {tracked ? (
                  <div className="mt-2 space-y-2">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault()
                        addScan(item)
                      }}
                    >
                      <div className="flex gap-2">
                        <Input
                          identifier
                          value={inputs[item.id] ?? ''}
                          onChange={(e) =>
                            setInputs((current) => ({ ...current, [item.id]: e.target.value }))
                          }
                          placeholder={`Scan or type a ${kindLabel} and press Enter`}
                          aria-label={`${kindLabel} for ${item.productName}`}
                        />
                        <Button type="submit" variant="secondary">
                          Add
                        </Button>
                      </div>
                    </form>
                    {list.length > 0 ? (
                      <ul className="divide-y divide-border rounded-md border border-border">
                        {list.map((s) => (
                          <li
                            key={s.imei}
                            className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm"
                          >
                            <span className="font-identifier">{s.imei}</span>
                            <span className="flex items-center gap-2">
                              {s.needsAccept && !s.acceptBadChecksum ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() =>
                                    setScans((current) => ({
                                      ...current,
                                      [item.id]: (current[item.id] ?? []).map((x) =>
                                        x.imei === s.imei ? { ...x, acceptBadChecksum: true } : x,
                                      ),
                                    }))
                                  }
                                >
                                  Check digit fails — accept as printed
                                </Button>
                              ) : null}
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={`Remove ${s.imei}`}
                                onClick={() =>
                                  setScans((current) => ({
                                    ...current,
                                    [item.id]: (current[item.id] ?? []).filter(
                                      (x) => x.imei !== s.imei,
                                    ),
                                  }))
                                }
                              >
                                <Trash2 />
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={(lines.length === 0 && !hasTyped) || !resolvedLocationId || unresolved}
            loading={receive.isPending}
            onClick={submitReceive}
          >
            Receive into stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditDialog({
  order,
  open,
  onClose,
  onRecordPayment,
}: {
  order: OrderDetail
  open: boolean
  onClose: () => void
  onRecordPayment: (owedCents: number) => void
}) {
  const toast = useToast()
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [vendor, setVendor] = useState(order.vendor)
  const [invoiceRef, setInvoiceRef] = useState(order.invoiceRef ?? '')
  const [locationId, setLocationId] = useState(order.expectedLocationId ?? '')
  const [note, setNote] = useState(order.note ?? '')
  const [shipping, setShipping] = useState(
    order.shippingCents === null ? '' : (order.shippingCents / 100).toFixed(2),
  )
  const [tax, setTax] = useState(order.taxCents === null ? '' : (order.taxCents / 100).toFixed(2))
  const [paymentStatus, setPaymentStatus] = useState<VendorPaymentStatus>(order.paymentStatus)
  const [allocateShipping, setAllocateShipping] = useState(order.allocateShipping)
  const [allocateTax, setAllocateTax] = useState(order.allocateTax)
  const toCents = (v: string) => (v.trim() === '' ? null : Math.round(Number(v) * 100))
  const extrasValid = [shipping, tax].every((v) => v.trim() === '' || Number(v) >= 0)

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
    enabled: open,
  })

  const { data: balance } = useQuery({
    queryKey: queryKeys.expenses.balance(order.id),
    queryFn: () => api.get<OrderBalance>(`/outgoing-payments/order-balance/${order.id}`),
    enabled: open && can('expenses:read'),
  })
  // The total as it will be once saved: shipping and tax in this form replace the stored ones.
  const owedCents =
    paymentStatus === 'paid' && balance
      ? Math.max(
          0,
          balance.totalCents -
            (order.shippingCents ?? 0) -
            (order.taxCents ?? 0) +
            (extrasValid ? (toCents(shipping) ?? 0) + (toCents(tax) ?? 0) : 0) -
            balance.paidCents,
        )
      : 0
  const canRecord = can('expenses:manage')

  // `recordAfter` is the amount to open the payment form with once saved, if asked for.
  const save = useMutation({
    mutationFn: (_recordAfter?: number) =>
      api.patch(`/purchase-orders/${order.id}`, {
        vendor: vendor.trim(),
        invoiceRef: invoiceRef.trim() || null,
        expectedLocationId: locationId || null,
        note: note.trim() || null,
        shippingCents: toCents(shipping),
        taxCents: toCents(tax),
        paymentStatus,
        allocateShipping,
        allocateTax,
      }),
    onSuccess: async (_, recordAfter) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.balance(order.id) })
      toast.success('Order updated')
      onClose()
      if (recordAfter !== undefined) onRecordPayment(recordAfter)
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {order.poNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Vendor">
            {(props) => (
              <Input {...props} value={vendor} onChange={(e) => setVendor(e.target.value)} />
            )}
          </Field>
          <Field label="Invoice / PO reference">
            {(props) => (
              <Input
                {...props}
                value={invoiceRef}
                onChange={(e) => setInvoiceRef(e.target.value)}
              />
            )}
          </Field>
          <Field label="Arriving at">
            {(props) => (
              <Select
                value={locationId || 'none'}
                onValueChange={(v) => setLocationId(v === 'none' ? '' : v)}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Decide when received</SelectItem>
                  {(locations ?? [])
                    .filter((l) => l.isActive)
                    .map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Shipping">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  className="text-end"
                />
              )}
            </Field>
            <Field label="Tax">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={0}
                  step="0.01"
                  value={tax}
                  onChange={(e) => setTax(e.target.value)}
                  className="text-end"
                />
              )}
            </Field>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Switch
                id="edit-allocate-shipping"
                checked={allocateShipping}
                onCheckedChange={setAllocateShipping}
              />
              <label htmlFor="edit-allocate-shipping">Add shipping to item cost</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="edit-allocate-tax"
                checked={allocateTax}
                onCheckedChange={setAllocateTax}
              />
              <label htmlFor="edit-allocate-tax">Add tax to item cost</label>
            </div>
            <p className="text-xs text-foreground-muted">
              Split across the items by value, so a part-delivery takes its share. Applies to items
              received from now on; ones already received keep their cost.
            </p>
          </div>
          <Field
            label="Payment"
            hint="Cost entries made on receiving start with this status. Changing it also updates the entries already made for this order."
          >
            {(props) => (
              <Select
                value={paymentStatus}
                onValueChange={(v) => setPaymentStatus(v as VendorPaymentStatus)}
              >
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VENDOR_PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {humanizeStatus(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          {owedCents > 0 ? <UnpaidOrderWarning owedCents={owedCents} /> : null}
          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {owedCents > 0 && canRecord ? (
            <Button
              variant="secondary"
              disabled={vendor.trim() === '' || !extrasValid}
              loading={save.isPending}
              onClick={() => save.mutate(owedCents)}
            >
              Save and record payment
            </Button>
          ) : null}
          <Button
            variant="primary"
            disabled={vendor.trim() === '' || !extrasValid}
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function FinishDialog({
  order,
  action,
  onClose,
}: {
  order: OrderDetail
  action: 'cancel' | 'close' | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')

  const finish = useMutation({
    mutationFn: () =>
      api.post(`/purchase-orders/${order.id}/${action}`, { note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      toast.success(action === 'cancel' ? 'Order cancelled' : 'Order closed')
      setNote('')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not update the order',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={action !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {action === 'cancel' ? `Cancel ${order.poNumber}?` : `Close ${order.poNumber}?`}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground-muted">
            {action === 'cancel'
              ? 'Nothing has been received, so its pending items simply go away.'
              : 'What has already arrived stays in stock. The items still pending will no longer be expected.'}
          </p>
          <Field label="Note">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Keep order
          </Button>
          <Button variant="primary" loading={finish.isPending} onClick={() => finish.mutate()}>
            {action === 'cancel' ? 'Cancel order' : 'Close order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
